// src/views/dashboard/ErrorLog.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { IconSearch } from '@tabler/icons-react';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import moment from 'moment';
import toast from 'react-hot-toast';
import { getDateRangeFromQuickOption } from 'utils/common';
import getAxiosInstance from 'api';

const StyledTableCell = styled(TableCell)(() => ({
  backgroundColor: '#FFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: 14,
  fontWeight: 500,
}));

const StyledTableCellHead = styled(TableCell)(() => ({
  backgroundColor: '#F0F0F0',
  color: 'rgba(33,33,32,0.6)',
  border: '1px solid #DEDEDE',
}));

const OutlineInputStyle = styled(OutlinedInput)(() => ({
  width: 250,
  padding: '4px 8px',
  borderRadius: 8,
  border: '1px solid rgba(33,33,32,0.1)',
}));
const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const headCells = [
  { id: 'consentId', label: 'Consent ID' },
  { id: 'bankName', label: 'Bank Name' },
  { id: 'businessPartner', label: 'Business Partner' },
  { id: 'consentInitiator', label: 'Channel' },
  { id: 'accountNumber', label: 'Account Number' },
  { id: 'journey', label: 'Journey' },
  { id: 'uploadType', label: 'Upload Type' },
  { id: 'createdAt', label: 'Created At' },
  { id: 'status', label: 'Status' },
];
const type = [
  { label: 'All', value: '' },
  { label: 'User', value: 'User' },
  { label: 'Online', value: 'Online' },
  { label: 'API', value: 'API' }
];

export default function ErrorLog() {
  const theme = useTheme();

  // Filtering & sort state
  const [dateRange, setDateRange] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [filterLoading, setFilterLoading] = useState(false);
  const [quickRangeValue, setQuickRangeValue] = useState('');
  // Pagination state
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  // Data state
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
 const [filter, setFilter] = useState({
    type: type[0],
  });
  // Fetch function
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
        endDate: moment(dateRange[1]).format('YYYY-MM-DD'),
        page: page + 1,
        limit,
        sortBy: orderBy,
        sortDirection: order,
        filter: {
          consentInitiator: filter.type?.value || '',
        },
        // optionally pass search: search.trim() || undefined
      };

      const res = await getAxiosInstance(
        'adminOrchestrator/app/getConsentDashboardData',
        'post',
        payload,
        { 'Content-Type': 'application/json' }
      );

      if (res.data.codes === 200 && res.data.data) {
        setRows(res.data.data.items || []);
        setTotalRows(res.data.data.total || 0);
      } else {
        toast.error(res.data.message || 'Failed to load logs');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, page, limit, order, orderBy, search, filter]);

  // Trigger fetch on deps change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sort handler
  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  // Filter change handler
  const handleFilterChange = (key, value) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(0); // Reset to first page on filter change
  }

  return (
    <Box>
      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        display={'flex'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
        rowGap={1.5}
        mb={2}
      >
        <Typography variant="h4">Logs</Typography>
        <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} rowGap={1} columnGap={1}>
          <FormControl size="small" sx={{ width: 170, marginRight: '1rem' }}>
            <Select
              value={filter.type?.value || ''}
              onChange={(event) => {
                const selectedValue = event.target.value;
                const selectedOption = type.find((t) => t.value === selectedValue) || type[0];
                handleFilterChange('type', selectedOption);
              }}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              {type.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 170, marginRight: '1rem' }}>
            <Select
              value={quickRangeValue || ''}
              onChange={(event) => {
                const newValue = event.target.value;
                setQuickRangeValue(newValue);
                if (!newValue) return;
                const range = getDateRangeFromQuickOption(newValue);
                if (!range) return;
                setDateRange([range.start, range.end]);
              }}
              displayEmpty
              inputProps={{ 'aria-label': 'Quick Range' }}
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value="">Quick Range</MenuItem>
              {quickRanges.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangePicker
            appearance="default"
            value={dateRange}
            onChange={(vals) => {
              setDateRange([vals[0], vals[1]]);
              setQuickRangeValue('');
            }}
            showOneCalendar
          />
        </Box>
      </Stack>

      {/* Table */}
      <Card>
        {loading ? (
          <Box p={3} display="flex" justifyContent="center">
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCells.map((cell) => (
                      <StyledTableCellHead
                        key={cell.id}
                        sortDirection={orderBy === cell.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === cell.id}
                          direction={
                            orderBy === cell.id ? order.toLowerCase() : 'asc'
                          }
                          onClick={(e) => handleRequestSort(e, cell.id)}
                        >
                          {cell.label}
                          {orderBy === cell.id && (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'DESC'
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </Box>
                          )}
                        </TableSortLabel>
                      </StyledTableCellHead>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row, idx) => (
                      <TableRow hover key={idx}>
                        <StyledTableCell>{row.consentId}</StyledTableCell>
                        <StyledTableCell>{row.bankName}</StyledTableCell>
                        <StyledTableCell>{row.businessName}</StyledTableCell>
                        <StyledTableCell>
  {row.consentInitiator === 'User' ? 'Connect' : row.consentInitiator === 'Online' ? 'Dashboard' : row.consentInitiator || 'N/A'}
</StyledTableCell>
                        <StyledTableCell>
                          {row.accountNumber?.replace(/.(?=.{4})/g, 'x')}
                        </StyledTableCell>
                        <StyledTableCell>{row.requestType}</StyledTableCell>
                        <StyledTableCell>{row.uploadType}</StyledTableCell>
                        <StyledTableCell>
                          {moment(row.createdAt).format('DD MMM YYYY, HH:mm')}
                        </StyledTableCell>
                        <StyledTableCell>{row.status}</StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={headCells.length} align="center">
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              rowsPerPage={limit}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
